const CX = 100;
const CY = 100;
const R = 88;

const LAT_LINES = [
  { ry: 14, cy: CY },
  { ry: 10, cy: CY - 38 },
  { ry: 10, cy: CY + 38 },
];

const MERIDIAN_RX = [22, 44, 66, 84];

const EQUATOR_RY = 14;
const STREAM_OUTSIDE = 18;
const EQUATOR = { cx: CX, cy: CY, rx: R, ry: EQUATOR_RY };

const DATA_DUR_SCALE = 1 / 0.75;
const MERIDIAN_DUR_SCALE = (1 / 0.65) * DATA_DUR_SCALE;

const LATTROP_EQUATOR_DUR = 3.6 * DATA_DUR_SCALE;
const LATTROP_EQUATOR_BEGIN = 1.75 * DATA_DUR_SCALE;
const LATTROP_EQUATOR_KEY_POINTS = '0;0.07;0.93;1';
const LATTROP_EQUATOR_KEY_TIMES = '0;0.32;0.68;1';

function meridianArcPath(rx, { side = 1, down = true } = {}) {
  const sweep = side >= 0 ? 1 : 0;
  if (down) {
    return `M ${CX} ${CY - R} A ${rx} ${R} 0 1 ${sweep} ${CX} ${CY + R}`;
  }
  return `M ${CX} ${CY + R} A ${rx} ${R} 0 1 ${sweep} ${CX} ${CY - R}`;
}

function equatorLoopPath({ entryExit = false } = {}) {
  const { cx, cy, rx, ry } = EQUATOR;
  const left = cx - rx;
  const right = cx + rx;
  const frontArc = `A ${rx} ${ry} 0 1 1 ${right} ${cy}`;
  const backArc = `A ${rx} ${ry} 0 1 1 ${left} ${cy}`;

  if (!entryExit) {
    return [`M ${left} ${cy}`, frontArc, backArc].join(' ');
  }

  return [
    `M ${left - STREAM_OUTSIDE} ${cy}`,
    `L ${left} ${cy}`,
    frontArc,
    backArc,
    `L ${right + STREAM_OUTSIDE} ${cy}`,
  ].join(' ');
}

function meridianLaneTiming(rx, lane) {
  const seed = rx * 19 + lane * 43;
  const baseDur = 0.42 + ((seed % 53) / 53) * 0.58;
  return {
    dur: baseDur * MERIDIAN_DUR_SCALE,
    begin: ((seed * 11) % 97) / 97 * 1.6 * MERIDIAN_DUR_SCALE,
    r: 1.45 + ((seed % 9) / 9) * 0.75,
    down: seed % 2 === 0,
    side: (seed >> 1) % 2 === 0 ? 1 : -1,
  };
}

function uid(prefix) {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
}

function Dot({
  path, dur, begin = 0, r = 2.2, keyPoints, keyTimes,
}) {
  return (
    <circle r={r} className="globe-data-dot">
      <animateMotion
        dur={`${dur}s`}
        begin={`${begin}s`}
        repeatCount="indefinite"
        path={path}
        calcMode={keyPoints ? 'linear' : undefined}
        keyPoints={keyPoints}
        keyTimes={keyTimes}
      />
    </circle>
  );
}

function Wireframe({ clipId }) {
  return (
    <g clipPath={`url(#${clipId})`} className="globe-wireframe">
      {LAT_LINES.map((line) => (
        <ellipse
          key={`lat-${line.cy}-${line.ry}`}
          cx={CX}
          cy={line.cy}
          rx={R}
          ry={line.ry}
          className="globe-line"
        />
      ))}
      {MERIDIAN_RX.map((rx) => (
        <ellipse
          key={`long-${rx}`}
          cx={CX}
          cy={CY}
          rx={rx}
          ry={R}
          className="globe-line"
        />
      ))}
    </g>
  );
}

function MeridianFastLaneLayer() {
  return (
    <>
      {MERIDIAN_RX.flatMap((rx) =>
        [0, 1].map((lane) => {
          const { dur, begin, r, down, side } = meridianLaneTiming(rx, lane);
          return (
            <Dot
              key={`${rx}-${lane}`}
              path={meridianArcPath(rx, { side, down })}
              dur={dur}
              begin={begin}
              r={r}
            />
          );
        }),
      )}
    </>
  );
}

function EquatorStreamLayer() {
  const equatorPath = equatorLoopPath({ entryExit: true });
  return (
    <>
      <Dot
        path={equatorPath}
        dur={LATTROP_EQUATOR_DUR}
        keyPoints={LATTROP_EQUATOR_KEY_POINTS}
        keyTimes={LATTROP_EQUATOR_KEY_TIMES}
      />
      <Dot
        path={equatorPath}
        dur={LATTROP_EQUATOR_DUR}
        begin={LATTROP_EQUATOR_BEGIN}
        r={1.75}
        keyPoints={LATTROP_EQUATOR_KEY_POINTS}
        keyTimes={LATTROP_EQUATOR_KEY_TIMES}
      />
    </>
  );
}

export default function GlobeAnimation({
  size = 160,
  className = '',
  label = 'GIS Liberty',
}) {
  const clipId = uid('globe-clip');

  return (
    <svg
      className={`globe-animation ${className}`.trim()}
      viewBox="0 0 200 200"
      width={size}
      height={size}
      role="img"
      aria-label={label}
    >
      <defs>
        <clipPath id={clipId}>
          <circle cx={CX} cy={CY} r={R} />
        </clipPath>
        <filter id={`${clipId}-glow`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="1.5" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <g>
        <Wireframe clipId={clipId} />
        <circle cx={CX} cy={CY} r={R} className="globe-outline" />
      </g>
      <g clipPath={`url(#${clipId})`} filter={`url(#${clipId}-glow)`}>
        <MeridianFastLaneLayer />
      </g>
      <g filter={`url(#${clipId}-glow)`}>
        <EquatorStreamLayer />
      </g>
    </svg>
  );
}
